/**
 * "Pick a Game to Create" — the template chooser.
 *
 * Built from screen_game_template_selection.png: a framed foliage plate
 * (src/assets/screens/template_bg.png) with a 2x2 grid of template cards on top. The
 * thumbnails come from the service (/coder/templates/:id/screenshot), which keeps them
 * next to the template code they illustrate — adding a template is one directory on the
 * server, with nothing to add here.
 *
 * Only the platformer is playable. The other three appear because the grid is part of the
 * design and one lonely card does not read as a product, but they are marked "Coming
 * soon" and cannot be selected: a tap that silently does nothing looks like a bug,
 * whereas a disabled card reads as scope.
 */

import React, { useEffect, useState } from "react";
import {
  View,
  Text,
  Pressable,
  Image,
  StyleSheet,
  ImageBackground,
  ActivityIndicator,
} from "react-native";
import { colors, textStyle } from "../styles";
import { useNavigation } from "../navigation/useNavigation";
import { getVoiceSocketUrl } from "../utils/AppConfig";

import TEMPLATE_BG from "../assets/screens/template_bg.png";

interface Template {
  id: string;
  name: string;
  tagline: string;
  available: boolean;
}

/**
 * Shown until the catalogue arrives.
 *
 * Hard-coded to the same four the server returns so the grid does not pop in: on a fast
 * local connection the fetch resolves in a few ms and an empty flash is more jarring than
 * a placeholder that is immediately replaced.
 */
const FALLBACK: Template[] = [
  { id: "platformer", name: "Platformer", tagline: "Run, jump and collect", available: true },
  { id: "whack-a-mole", name: "Wack-a-Mole", tagline: "Tap them as they pop up", available: false },
  { id: "endless-runner", name: "Endless Runner", tagline: "Dodge and keep going", available: false },
  { id: "maze", name: "Maze", tagline: "Find your way out", available: false },
];

export default function GameTemplateScreen() {
  const { navigate, goBack } = useNavigation();
  const [templates, setTemplates] = useState<Template[]>(FALLBACK);
  const [selected, setSelected] = useState<string>("platformer");
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    let alive = true;
    fetch(`${getVoiceSocketUrl()}/coder/templates`)
      .then((r) => r.json())
      .then((d: { templates?: Template[] }) => {
        if (alive && Array.isArray(d.templates) && d.templates.length > 0) {
          setTemplates(d.templates);
        }
      })
      // The fallback above is already on screen, so a failed fetch costs nothing.
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const start = (id: string) => {
    setStarting(true);
    // The coder page owns session setup; it starts from the platformer regardless, which
    // is why no param is threaded through yet. When a second template becomes playable
    // this is where the id would travel.
    navigate("coder", {});
  };

  return (
    <ImageBackground source={TEMPLATE_BG} style={styles.root} resizeMode="cover">
      <Pressable style={styles.back} onPress={goBack} testID="templates-back">
        <Text style={styles.backIcon}>{"←"}</Text>
      </Pressable>

      <View style={styles.center}>
        <Text style={styles.title} testID="templates-title">
          Pick a Game to Create
        </Text>
        <Text style={styles.subtitle}>Choose a base mechanic to start!</Text>

        <View style={styles.grid}>
          {templates.map((t) => {
            const isSelected = t.id === selected;
            return (
              <Pressable
                key={t.id}
                style={[
                  styles.card,
                  isSelected && styles.cardSelected,
                  !t.available && styles.cardUnavailable,
                ]}
                disabled={!t.available || starting}
                onPress={() => {
                  setSelected(t.id);
                  start(t.id);
                }}
                testID={`template-${t.id}`}
              >
                <View style={styles.thumbWrap}>
                  <Image
                    source={{ uri: `${getVoiceSocketUrl()}/coder/templates/${t.id}/screenshot` }}
                    style={styles.thumb}
                    resizeMode="cover"
                  />
                  {isSelected && t.available ? (
                    <View style={styles.check} testID={`template-${t.id}-check`}>
                      <Text style={styles.checkIcon}>{"✓"}</Text>
                    </View>
                  ) : null}
                </View>
                <View style={styles.cardFooter}>
                  <Text style={styles.cardName}>{t.name}</Text>
                  {!t.available ? <Text style={styles.soon}>Coming soon</Text> : null}
                </View>
              </Pressable>
            );
          })}
        </View>

        {starting ? (
          <View style={styles.starting} testID="templates-starting">
            <ActivityIndicator color={colors.accent} />
            <Text style={styles.startingText}>Opening your game...</Text>
          </View>
        ) : null}
      </View>
    </ImageBackground>
  );
}

const styles = StyleSheet.create({
  // Matches the plate's cream so any letterboxing blends in.
  root: { flex: 1, backgroundColor: colors.gameLabCream },
  center: { flex: 1, alignItems: "center", justifyContent: "center", paddingHorizontal: 24 },

  back: {
    position: "absolute",
    top: 22,
    left: 22,
    width: 52,
    height: 52,
    borderRadius: 26,
    backgroundColor: colors.white,
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2,
  },
  backIcon: { fontSize: 24, color: "#0F1B3D", ...textStyle.heading },

  title: { fontSize: 46, color: "#0F1B3D", ...textStyle.heading, textAlign: "center" },
  subtitle: {
    fontSize: 19,
    color: "#5B6478",
    ...textStyle.body,
    marginTop: 4,
    marginBottom: 22,
  },

  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: 18,
    maxWidth: 940,
  },
  card: {
    width: 440,
    borderRadius: 20,
    backgroundColor: colors.white,
    overflow: "hidden",
    borderWidth: 3,
    borderColor: "transparent",
  },
  cardSelected: { borderColor: "#7C5CE0" },
  // Dimmed rather than hidden: the grid is part of the design.
  cardUnavailable: { opacity: 0.72 },

  thumbWrap: { position: "relative" },
  thumb: { width: "100%", height: 218, backgroundColor: "#DCEFFB" },
  check: {
    position: "absolute",
    top: 12,
    right: 12,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#7C5CE0",
    alignItems: "center",
    justifyContent: "center",
  },
  checkIcon: { color: colors.white, fontSize: 20, ...textStyle.heading },

  cardFooter: { paddingVertical: 14, alignItems: "center", gap: 2 },
  cardName: { fontSize: 22, color: "#0F1B3D", ...textStyle.heading },
  soon: { fontSize: 13, color: colors.textMuted, ...textStyle.body },

  starting: { flexDirection: "row", alignItems: "center", gap: 10, marginTop: 20 },
  startingText: { fontSize: 16, color: colors.textSecondary, ...textStyle.body },
});
